import { PRICING_PLANS } from '@wix/app-definition-ids';
import constants from '#packages/constants';
import * as coreBi from '#packages/coreBi';
import { languages } from '#packages/util';
import { getProtectionType } from '../../../utils/permissions';
import { isPageProtectedWithPassword } from '../../../utils/permissions';
import { membersGroupsApi } from '../../../utils/groupsApi';
import { hasLegacyAuthScreens } from '@/pages';
import { translate } from '#packages/i18n';
import { silentInstallApps } from '#packages/platform';
import { media } from '@/util';
import { isOnWixStudio } from '@wix/santa-editor-utils';
import experiments from 'experiment';
import { MEMBERS_AREA } from '@wix/app-definition-ids';

import type { EditorAPI } from '@/editorAPI';
import type {
  PermissionsTabDispatchProps,
  PermissionsTabOwnProps,
  PermissionsTabStateProps,
} from './permissionsTab';
import type {
  MapStateToProps,
  MapDispatchToProps,
  ThunkAction,
} from 'types/redux';
import type { PageSecurity } from 'types/documentServices';
import type { PermissionState } from '../../../utils/constants';
import type { Group } from '../../../utils/groupsApi';

export interface UpdatePageSecurityDataProps {
  pageSecurity: PageSecurity;
  privacyType: PermissionState;
}

export interface PageGroupData {
  groupsForPage: Required<Group>[];
  adminRestrictedPage: string[];
  availableGroups: Required<Group>[];
}

export interface RolesPanelProps {
  panelName: string;
  groups: Required<Group>[];
  headerTitle: string;
  selectedGroups: Required<Group>[];
  applyButtonLabel: string;
  applyButtonCallback: (groups: Required<Group>[]) => Promise<void>;
  addNewButtonLabel: string;
  addNewButtonCallback: () => void;
  disableAddNew: boolean;
  origin: string;
}

export interface OpenDashboardModalProps {
  path: string;
  onClose: () => void;
}

export interface OpenInstallPricingPlansModalProps {
  onAfterInstall: () => Promise<void>;
}

const AUTHENTICATION_APP = '1052fa8e-e3e2-46dc-ab66-8badb2d08fe9';

export const mapStateToProps: MapStateToProps<
  PermissionsTabStateProps,
  PermissionsTabOwnProps
> = ({ editorAPI }, { pageData }) => {
  const defaultLanguage = editorAPI.dsRead.generalInfo.getLanguage();
  const savedLanguage = pageData?.pageSecurity?.dialogLanguage;

  const getShouldShowProtectionType = () => {
    const dsRead = editorAPI.dsRead;
    const pageRef = dsRead.pages.getFocusedPage();
    const routerRef = dsRead.routers.getRouterRef.byPage(pageRef);

    return (
      !routerRef ||
      dsRead.routers.get.byRef(routerRef)?.config.type !== 'private'
    );
  };

  return {
    shouldShowProtectionType: getShouldShowProtectionType(),
    initialFocusedProtectionType: getProtectionType(editorAPI, pageData),
    // seems like some older code uses it as a flag to check whether page is password protected
    dialogLanguage: savedLanguage ? savedLanguage : defaultLanguage,
    isPageProtectedWithPassword: isPageProtectedWithPassword(
      editorAPI,
      pageData,
    ),
    metaSiteId: editorAPI.dsRead.generalInfo.getMetaSiteId(),
    isSiteSaved:
      !editorAPI.dsRead.generalInfo.isFirstSave() &&
      !editorAPI.dsRead.generalInfo.isDraft(),
    permissions: editorAPI.dsRead.generalInfo.getUserPermissions(),
    isPricingPlansAppInstalled:
      editorAPI.documentServices.tpa.app.isInstalled(PRICING_PLANS),
    isStudio: isOnWixStudio(),
  };
};

const getEditorAPI: ThunkAction<EditorAPI> = (
  dispatch,
  getState,
  { editorAPI },
) => editorAPI;

const installAuthenticationAppIfNeeded = function (editorAPI: EditorAPI) {
  const isAppInstalled = editorAPI.tpa.app.isInstalled(AUTHENTICATION_APP);
  if (isAppInstalled) {
    return;
  }
  if (hasLegacyAuthScreens(editorAPI)) {
    return;
  }
  editorAPI.tpa.installAppIfNeeded(AUTHENTICATION_APP);
};

const uninstallAuthenticationAppIfNeeded = function (
  editorAPI: EditorAPI,
  pageId: string,
) {
  const isSiteMembersAuthUninstallEnabled = experiments.isOpen(
    isOnWixStudio()
      ? 'specs.ident.shouldUninstallIdentityAuthAppV2' // V2 is for studio (Aligned with members-area FTs)
      : 'specs.ident.shouldUninstallIdentityAuthAppV3',
  );
  if (!isSiteMembersAuthUninstallEnabled) {
    return;
  }

  const isProtectedPageExists = editorAPI.pages
    .getPagesData()
    .some((page) => page.pageSecurity.requireLogin && page.id !== pageId);
  if (isProtectedPageExists) {
    return;
  }
  const isMemebersAreaAppInstalled =
    editorAPI.tpa.app.isInstalled(MEMBERS_AREA);
  if (isMemebersAreaAppInstalled) {
    return;
  }
  const appData = editorAPI.platform.getAppDataByAppDefId(AUTHENTICATION_APP);
  editorAPI.platform.applications.uninstall(appData, false);
};

export const mapDispatchToProps: MapDispatchToProps<
  PermissionsTabDispatchProps,
  PermissionsTabOwnProps
> = (dispatch, { pageData }) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);
  const pageId = pageData.id;
  const languageCode = languages.getLanguageCode();

  const openHelpCenter = (helpId: string) => {
    editorAPI.panelManager.openHelpCenter(helpId, null, {
      origin: constants.BI.HELP.ORIGIN.PAGES_PANEL,
      learn_more: true,
    });
  };

  const updatePageSecurityData = ({
    pageSecurity,
    privacyType,
  }: UpdatePageSecurityDataProps) => {
    const isSiteMembersAuthInstallationEnabled = experiments.isOpen(
      isOnWixStudio()
        ? 'specs.ident.shouldInstallIdentityAuthAppV2' // V2 is for studio (Aligned with members-area FTs)
        : 'specs.ident.shouldInstallIdentityAuthAppV3',
    );
    if (isSiteMembersAuthInstallationEnabled) {
      if (pageSecurity.requireLogin) {
        installAuthenticationAppIfNeeded(editorAPI);
      } else {
        uninstallAuthenticationAppIfNeeded(editorAPI, pageId);
      }
    }
    editorAPI.pages.data.update(pageId, { pageSecurity });
    editorAPI.bi.event(
      coreBi.events.topbar.pages.top_bar_PAGES_settings_privacy_changed,
      {
        privacy: privacyType,
        page_id: pageId,
      },
    );
  };

  const updatePagePassword = (password: string) => {
    return editorAPI.pages.permissions.updatePassword(pageId, password);
  };

  const removePagePassword = () => {
    return editorAPI.pages.permissions.removePassword(pageId);
  };

  const fetchPageGroupData = async (pageId: string): Promise<PageGroupData> => {
    const groupsApi = membersGroupsApi(editorAPI);

    const [groupsForPage, adminRestrictedPage, availableGroups] =
      await Promise.all([
        groupsApi.getGroupsForPage({ pageId }),
        groupsApi.getPagesOnlyForAdmin(),
        groupsApi.getAvailableRoles(),
      ]);

    return {
      groupsForPage,
      adminRestrictedPage,
      availableGroups,
    };
  };

  const updateGroupsPermissions = async (groupsToUpdate: Group[]) => {
    const groupsApi = membersGroupsApi(editorAPI);
    await groupsApi.updateGroupsPermissions({ pageId, groupsToUpdate });
  };

  const openRolesPanel = (panelProps: RolesPanelProps) => {
    editorAPI.panelManager.openPanel(
      'panels.toolPanels.rolesManagerPanel',
      panelProps,
      true,
    );
  };

  const openDashboardModal = ({ onClose, path }: OpenDashboardModalProps) => {
    editorAPI.saveManager.saveInBackground(
      () => {
        editorAPI.panelManager.closePanelByName(
          'panels.toolPanels.rolesManagerPanel',
          'out_of_focus',
        );
        const metaSiteId = editorAPI.dsRead.generalInfo.getMetaSiteId();
        const url = `//www.wix.com/dashboard/${metaSiteId}${path}`;
        const modalProps = {
          url,
          onClose,
          helpId: '',
          width: '100%',
          height: '100%',
        };
        editorAPI.panelManager.openPanel(
          'tpa.compModals.tpaSettingsModal',
          modalProps,
          true,
        );
      },
      () => {},
      'openRolesPageModal',
    );
  };

  const openPricingPlansInAppMarket = () => {
    const panelProps = {
      urlParams: {
        openAppDefId: PRICING_PLANS,
        openMarketOrigin: 'pageSettings',
        appDefinitionId: PRICING_PLANS,
      },
    };

    editorAPI.panelManager.openPanel(
      'tpa.compPanels.appMarketPanel',
      panelProps,
      true,
    );
  };

  const installPricingPlans = async () => {
    return silentInstallApps(editorAPI, [PRICING_PLANS], {
      shouldRestrictActions: true,
      leavePanelsOpen: true,
    });
  };

  const openInstallPricingPlansModal = ({
    onAfterInstall,
  }: OpenInstallPricingPlansModalProps) =>
    editorAPI.platform.panels.openPromotionalPanel({
      titleText: translate(
        'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Title',
      ),
      contentArray: [
        translate(
          'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Item_One',
        ),
        translate(
          'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Item_Two',
        ),
        translate(
          'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Item_Three',
        ),
      ],
      mainActionText: translate(
        'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Main_Button',
      ),
      secondaryActionText: translate(
        'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Secondary_Button',
      ),
      shouldShowIllustration: true,
      illustration: media.getMediaUrl(
        '/wixPricingPlans/pricing-plans-promotional-icon.svg',
      ),
      primaryAction: async () => {
        await installPricingPlans();
        await onAfterInstall();
      },
      secondaryAction: () => {
        window.open(
          `https://support.wix.com/${languageCode}/article/pricing-plans-making-site-pages-members-only`,
        );
      },
      footnote: translate(
        'NewPages_Panel_Permissions_Install_Pricing_Plans_Modal_Footnote',
      ),
    });

  return {
    openDashboardModal,
    openRolesPanel,
    openHelpCenter,
    updatePageSecurityData,
    updatePagePassword,
    removePagePassword,
    fetchPageGroupData,
    updateGroupsPermissions,
    openPricingPlansInAppMarket,
    openInstallPricingPlansModal,
  };
};
